import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Home } from "lucide-react";

/**
 * Global navigation header buttons: Back / Forward / Home.
 *
 * Always-visible on every screen (mobile + desktop). Sits between the
 * HidroVerse logo and the Hidro AI button — never overlapped because
 * its parent header is sticky/fixed with z-index 60.
 *
 * Behaviours:
 *  - Back: navigates -1 in history. If no entry exists, falls back to
 *    `homePath` (so the user can never get "stuck" outside the app).
 *  - Forward: only renders when there IS forward history (we track this
 *    by snapshotting `history.state.idx` between navigations).
 *  - Home: hard-navigates to `homePath` (`/portal` for clients, `/` otherwise).
 *  - Swipe-from-left edge (mobile only): triggers Back when the user
 *    drags >60 px rightwards starting from the left 24 px of the screen.
 */
export default function NavHistoryButtons({ portalMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const homePath = portalMode ? "/portal" : "/";

  // Track whether forward-history exists so we can hide the forward arrow
  // when there's nothing to go forward to. React-Router exposes a numeric
  // idx in history.state — we compare it to the highest idx we've ever
  // seen on this session to know when forward is meaningful.
  const [hasForward, setHasForward] = useState(false);
  const maxIdxRef = useRef(0);
  useEffect(() => {
    const idx = (window.history.state && window.history.state.idx) ?? 0;
    if (idx > maxIdxRef.current) maxIdxRef.current = idx;
    setHasForward(idx < maxIdxRef.current);
  }, [location.key]);

  const goBack = () => {
    // window.history.length is 1 when we landed straight on this page.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(homePath);
    }
  };
  const goForward = () => navigate(1);
  const goHome = () => navigate(homePath);

  // Edge-swipe gesture (mobile only)
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let armed = false;
    const onStart = (e) => {
      const t = e.touches[0];
      armed = t.clientX <= 24; // only fire from the left edge
      startX = t.clientX;
      startY = t.clientY;
    };
    const onMove = (e) => {
      if (!armed) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (dx > 60 && dy < 40) {
        armed = false;
        goBack();
      }
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homePath]);

  const onHome = location.pathname === homePath || location.pathname === "/";

  return (
    <div className="flex items-center gap-0.5" data-testid="nav-history-buttons">
      <button
        type="button"
        onClick={goBack}
        className="p-1.5 rounded text-[#a0aabf] hover:text-white hover:bg-white/5 transition-colors"
        title="Back"
        aria-label="Back"
        data-testid="global-back"
      >
        <ArrowLeft className="w-4 h-4" />
      </button>
      {hasForward && (
        <button
          type="button"
          onClick={goForward}
          className="p-1.5 rounded text-[#a0aabf] hover:text-white hover:bg-white/5 transition-colors"
          title="Forward"
          aria-label="Forward"
          data-testid="global-forward"
        >
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
      <button
        type="button"
        onClick={goHome}
        disabled={onHome}
        className={`p-1.5 rounded transition-colors ${
          onHome ? "text-[#1fb6c1] bg-[#1fb6c1]/10" : "text-[#a0aabf] hover:text-white hover:bg-white/5"
        }`}
        title="Home"
        aria-label="Home"
        data-testid="global-home"
      >
        <Home className="w-4 h-4" />
      </button>
    </div>
  );
}
